import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Select,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Grid,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { useCustomFields } from '../../../contexts/CustomFieldsContext';
import { FieldCorrelation } from '../../../types/customFields';

interface TrendData {
  date: string;
  [key: string]: any;
}

const TimeRanges = {
  '7d': '7 Days',
  '30d': '30 Days',
  '90d': '90 Days',
  '1y': '1 Year',
} as const;

type TimeRange = keyof typeof TimeRanges;

const TrackingTrends: React.FC = () => {
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [trendData, setTrendData] = useState<TrendData[]>([]);
  const [correlations, setCorrelations] = useState<FieldCorrelation[]>([]);
  const { customFields } = useCustomFields();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  const trackableFields = customFields.filter(
    (field) => field.field_type === 'number' || field.field_type === 'scale'
  );

  useEffect(() => {
    if (selectedFields.length > 0) {
      loadTrendData();
    }
  }, [selectedFields, timeRange]);

  const loadTrendData = async () => {
    // In a real implementation, this would fetch data from your API
    // This is just generating sample data
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 365;
    const data: TrendData[] = [];

    for (let i = days; i >= 0; i--) {
      const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
      const entry: TrendData = { date };

      selectedFields.forEach((fieldId) => {
        const field = customFields.find((f) => f.id === fieldId);
        if (field) {
          // Generate random sample data
          entry[field.id] = Math.random() * 100;
        }
      });

      data.push(entry);
    }

    setTrendData(data);
    if (selectedFields.length >= 2) {
      calculateCorrelations(data);
    }
  };

  const calculateCorrelations = (data: TrendData[]) => {
    const correlations: FieldCorrelation[] = [];

    for (let i = 0; i < selectedFields.length; i++) {
      for (let j = i + 1; j < selectedFields.length; j++) {
        const field1 = selectedFields[i];
        const field2 = selectedFields[j];

        // Calculate Pearson correlation coefficient
        const values1 = data.map((d) => d[field1]);
        const values2 = data.map((d) => d[field2]);

        const correlation = calculatePearsonCorrelation(values1, values2);
        
        correlations.push({
          id: `${field1}-${field2}`,
          field_id_1: field1,
          field_id_2: field2,
          correlation_type: correlation > 0 ? 'positive' : correlation < 0 ? 'negative' : 'neutral',
          correlation_strength: Math.abs(correlation),
          time_period: timeRange,
          created_at: new Date().toISOString(),
        });
      }
    }

    setCorrelations(correlations);
  };

  const calculatePearsonCorrelation = (x: number[], y: number[]): number => {
    const n = x.length;
    const sum1 = x.reduce((a, b) => a + b, 0);
    const sum2 = y.reduce((a, b) => a + b, 0);
    const sum1Sq = x.reduce((a, b) => a + b * b, 0);
    const sum2Sq = y.reduce((a, b) => a + b * b, 0);
    const pSum = x.reduce((a, b, i) => a + b * y[i], 0);

    const num = pSum - (sum1 * sum2 / n);
    const den = Math.sqrt((sum1Sq - sum1 * sum1 / n) * (sum2Sq - sum2 * sum2 / n));

    return den === 0 ? 0 : num / den;
  };

  const getFieldColor = (index: number): string => {
    const colors = ['#2196F3', '#4CAF50', '#F44336', '#FFC107', '#9C27B0', '#00BCD4'];
    return colors[index % colors.length];
  };

  const renderCorrelations = () => {
    if (correlations.length === 0) return null;

    return (
      <Box mt={8}>
        <Text fontSize="lg" fontWeight="medium" mb={4}>
          Correlations
        </Text>
        <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={4}>
          {correlations.map((correlation) => {
            const field1 = customFields.find((f) => f.id === correlation.field_id_1);
            const field2 = customFields.find((f) => f.id === correlation.field_id_2);
            if (!field1 || !field2) return null;

            return (
              <Box
                key={correlation.id}
                p={4}
                bg={bgColor}
                borderRadius="md"
                border="1px"
                borderColor={borderColor}
              >
                <VStack align="stretch" spacing={2}>
                  <Text fontSize="sm" color={textColor}>
                    {field1.name} vs {field2.name}
                  </Text>
                  <Stat>
                    <StatLabel>Correlation</StatLabel>
                    <StatNumber>
                      {(correlation.correlation_strength * 100).toFixed(1)}%
                    </StatNumber>
                    <StatHelpText>
                      <StatArrow
                        type={correlation.correlation_type === 'positive' ? 'increase' : 'decrease'}
                      />
                      {correlation.correlation_type.charAt(0).toUpperCase() +
                        correlation.correlation_type.slice(1)}
                    </StatHelpText>
                  </Stat>
                </VStack>
              </Box>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      <VStack spacing={4} align="stretch">
        <HStack spacing={4}>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as TimeRange)}
            w="200px"
          >
            {Object.entries(TimeRanges).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>

          <Select
            placeholder="Add metric"
            value=""
            onChange={(e) => {
              if (e.target.value && !selectedFields.includes(e.target.value)) {
                setSelectedFields([...selectedFields, e.target.value]);
              }
            }}
            w="200px"
          >
            {trackableFields
              .filter((field) => !selectedFields.includes(field.id))
              .map((field) => (
                <option key={field.id} value={field.id}>
                  {field.name}
                </option>
              ))}
          </Select>
        </HStack>

        {selectedFields.length > 0 && (
          <Box
            p={4}
            bg={bgColor}
            borderRadius="lg"
            border="1px"
            borderColor={borderColor}
            h="400px"
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => format(new Date(date), 'MMM d')}
                />
                <YAxis />
                <RechartsTooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <Box p={2} bg={bgColor} border="1px" borderColor={borderColor} borderRadius="md">
                          <Text fontWeight="medium">
                            {format(new Date(label), 'MMM d, yyyy')}
                          </Text>
                          {payload.map((entry: any, index: number) => {
                            const field = customFields.find((f) => f.id === entry.dataKey);
                            return (
                              <Text key={index} color={entry.color}>
                                {field?.name}: {entry.value.toFixed(1)}
                                {field?.unit ? ` ${field.unit}` : ''}
                              </Text>
                            );
                          })}
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                {selectedFields.map((fieldId, index) => {
                  const field = customFields.find((f) => f.id === fieldId);
                  if (!field) return null;

                  return (
                    <Line
                      key={fieldId}
                      type="monotone"
                      dataKey={fieldId}
                      name={field.name}
                      stroke={getFieldColor(index)}
                      dot={false}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}

        {renderCorrelations()}
      </VStack>
    </Box>
  );
};

export default TrackingTrends; 